import { Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2 as Renderer, ViewChild } from '@angular/core';
import {
  AuthenticationService,
  CreatePlantDeviceInput,
  CreatePlantUserPrefInput,
  GenericDeviceValidator,
  MonitorPrefEnumType,
  PlantConfigInput,
  PlantEmsEnumType,
  PlantGeoInput,
  PlantMonitoringEnumType,
  ProductModelService,
  Systems,
  UserPermissions,
  deviceSerialValidator,
  wait,
  GetLoggerDataQuery,
  AzzurroHubDataType,
  MonitoringModeInput,
  AHmonitoringModeEnum,
} from 'ngx-core-services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatStepper, StepperOrientation } from '@angular/material/stepper';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { first, tap } from 'rxjs/operators';
import { BrowserToastService, UiPromiseService } from 'ngx-zcs-components';

import { ChooseState } from '../create-block/create-block.model';
import { labels } from '../create-block/create.model';
import {
  connextSerialValidator,
  getLoggerState,
  hasPlantOwner,
  inverterListValidator,
  isConnextLoggerSelected,
  loggerFormValidator,
  plantUniqueEmailValidator,
  serialUniqueValidator,
  POWER_VALIDATOR,
  isStickLoggerSelected,
  azzurroHubSerialValidator,
} from '../create-firebase/create.utils';
import { PositionDetails } from '../components/maps-form/maps-form.models';
import { ConnextUsersForm } from '../users-section/users-section-models';
import { CreateAwsService } from './create-aws.service';
import { convertProductTypeNameToPlantDeviceType } from './create-aws.utils';
import { RemoveInverterFromPlant } from '../virtual-devices-section/virtual-devices-section.component';
import { azzurroHubDataQuery } from '../azzurro-hub-devices-section/query-constructor.query';

@UntilDestroy()
@Component({
  selector: 'create-aws-block',
  templateUrl: './create-aws.component.html',
  styleUrls: ['./create-aws.component.scss'],
})
export class CreateAwsComponent implements OnInit {
  @ViewChild('stepper') stepper?: MatStepper;

  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return { ...this._labels };
  }

  @Input() stepperOrientation: StepperOrientation = 'horizontal';
  @Input() systems$?: Observable<Systems | undefined>;

  @Output() wizardFinished = new EventEmitter<string>();
  @Output() wizardExit = new EventEmitter<void>();
  @Output() onReset = new EventEmitter<void>();

  monitoringForm = new FormGroup({
    monitoringPreference: new FormControl<ChooseState[]>([], loggerFormValidator({ mutuallyExclusive: false })),
    connextSerial: new FormControl('',
      deviceSerialValidator(['connext']),
      this.genericDeviceValidator.deviceTypeExists(['connext'], { connextFwMinVersion: '1.41.1954', certifiedPlant: true }),
    ),
    azzurroHubSerial: new FormControl('',
      deviceSerialValidator(['azzurro-hub']),
      this.genericDeviceValidator.deviceTypeExists(['azzurro-hub'], { certifiedPlant: true, azzurroHubFwMinVersion: '200.300' }),
    ),
    importSystemName: new FormControl(''),
  }, {
    validators: [connextSerialValidator, azzurroHubSerialValidator],
  });

  connextConfigured = false;
  azzurroHubConfigured = false;

  connextConfiguration = new FormGroup({
    plantName: new FormControl('', Validators.required),
    coordinates: new FormControl<PositionDetails | null>(null, Validators.required),
    plantPower: new FormControl<number | null>(null, [Validators.required, POWER_VALIDATOR]),
    plantType: new FormControl<number | null>(null, [Validators.required]),
    applied: new FormControl(false, Validators.requiredTrue),
    plantUid: new FormControl(''),
  });

  azzurroHubConfiguration = new FormGroup({
    plantName: new FormControl('', Validators.required),
    coordinates: new FormControl<PositionDetails | null>(null, Validators.required),
    contractualPower: new FormControl<number | null>(null, [Validators.required, POWER_VALIDATOR]),
    plantType: new FormControl<number | null>(null, [Validators.required]),
    monitoringMode: new FormControl<MonitoringModeInput | null>(null, Validators.required),
    applied: new FormControl(false, Validators.requiredTrue),
    plantUid: new FormControl(''),
    isOnline: new FormControl<boolean>(false, Validators.required),
  });

  virtualConfiguration = new FormGroup({
    plantName: new FormControl('', Validators.required),
    coordinates: new FormControl<PositionDetails | null>(null, Validators.required),
    meterInversion: new FormControl<boolean | undefined>(undefined),
  });

  readonly plantUsers = new FormGroup({
    users: new FormArray([
      new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email, plantUniqueEmailValidator]),
        userLevel: new FormControl(UserPermissions.Admin, Validators.required),
        isSelected: new FormControl(false),
        added: new FormControl(false, Validators.requiredTrue),
      }),
    ]),
    isModified: new FormControl(false),
  }, {
    validators: hasPlantOwner,
  });

  readonly addCurrentUser$ = this.authenticationService.user$.pipe(
    first(),
    tap((user) => {
      if (user && this.plantUsers.controls.users.controls.length) {
        this.plantUsers.controls.users.controls[0].setValue({
          email: user.email,
          userLevel: UserPermissions.Owner,
          isSelected: false,
          added: true,
        });

        this.plantUsers.controls.isModified.setValue(false);
        this.temporaryUser$.next(this.plantUsers as ConnextUsersForm);
      }
    }),
  );

  readonly temporaryUser$ = new BehaviorSubject<ConnextUsersForm | null>(null);

  readonly handleTemporaryUser$ = this.temporaryUser$.pipe(
    tap((_users) => {
      try {

        if (!_users) {
          return;
        }

        const users = _users.controls.users.value;
        if (users?.length) {
          this.plantUsers.controls.users.clear();
          users.forEach((user) => {
            this.plantUsers.controls.users.push(new FormGroup({
              email: new FormControl(user.email ?? null, [Validators.required, Validators.email, plantUniqueEmailValidator]),
              userLevel: new FormControl(user.userLevel ?? null, Validators.required),
              isSelected: new FormControl(false),
              added: new FormControl(true),
            }));
          });

          this.plantUsers.controls.users.push(new FormGroup({
            email: new FormControl('', [Validators.required, Validators.email, plantUniqueEmailValidator]),
            userLevel: new FormControl<number>(UserPermissions.Admin, Validators.required),
            isSelected: new FormControl(false),
            added: new FormControl(false),
          }));

          this.plantUsers.controls.isModified.setValue(false);
        }
      } catch {}
    }),
  )


  readonly inverterSyncValidators = [
    Validators.required,
    deviceSerialValidator(['inverter', 'meter', 'enerclic']),
    serialUniqueValidator,
  ];

  readonly inverterAsyncValidators = [
    this.genericDeviceValidator.deviceTypeExists(['inverter', 'meter', 'enerclic']),
    this.genericDeviceValidator.checkAvailableDeviceWithCertifiedPlant(),
  ];

  readonly connextInverterSyncValidators = [
    Validators.required,
    deviceSerialValidator(['inverter']),
    serialUniqueValidator,
  ];

  readonly EMSInverterAsyncValidators = [
    this.genericDeviceValidator.checkAvailableDeviceWithCertifiedPlant(),
  ];

  virtualDevicesConfig = new FormGroup({
    inverterList: new FormArray([
      new FormGroup({
        inverterSerial: new FormControl('', this.inverterSyncValidators, this.inverterAsyncValidators),
        added: new FormControl(false, Validators.requiredTrue),
        dismiss: new FormControl<RemoveInverterFromPlant | null>(null),
      }),
    ]),
  }, {
    validators: inverterListValidator,
  });

  connextDevicesConfig = new FormGroup({
    productionSensor: new FormGroup({
      productionSensorK: new FormControl<number | null>(null, [
        Validators.required,
        Validators.min(1),
        Validators.max(10),
      ]),
      id: new FormControl('', Validators.required),
      registered: new FormControl(false),
    }),
    inverterList: new FormArray([
      new FormGroup({
        inverterSerial: new FormControl('', this.connextInverterSyncValidators, this.EMSInverterAsyncValidators),
        inverterModbusId: new FormControl('', [
          Validators.required,
          Validators.min(1),
          Validators.max(255),
          Validators.pattern('^[0-9]*$'),
        ]),
        setup: new FormControl(false),
        registered: new FormControl(false),
      }),
    ]),
  });

  // Simplified array for Azzurro Hub devices
  azzurroHubDevices: string[] = [];

  onAzzurroHubDevicesChange(devices: string[]): void {
    this.azzurroHubDevices = devices;
  }

  readonly loggers: ChooseState[] = [];

  /**
 * If connext or azzurro hub logger is selected, this step is optional
 * If not, it is required, and the user must add at least one inverter
 */
  get isDevicesConfigInvalid() {
    return (this.isConnextLoggerSelected || this.isAzzurroHubLoggerSelected)
      ? false
      : (this.virtualDevicesConfig.controls.inverterList.length <= 1);
  }


  get monitoringPreference() {
    return this.monitoringForm.controls.monitoringPreference;
  }

  get isMonitoringFormInvalidOrPending() {
    return this.monitoringForm.invalid || this.monitoringForm.pending;
  }

  get connextSerial() {
    return this.monitoringForm.controls.connextSerial;
  }

  get azzurroHubSerial() {
    return this.monitoringForm.controls.azzurroHubSerial;
  }

  get isConnextLoggerSelected() {
    return isConnextLoggerSelected(this.monitoringPreference?.value);
  }

  get isAzzurroHubLoggerSelected() {
    return this.monitoringPreference?.value?.some((logger) => logger.name === 'azzurroHub' && logger.checked$.value) ?? false;
  }

  get isOtherLoggersSelected() {
    return isStickLoggerSelected(this.monitoringPreference?.value);
  }

  get connextInverterList() {
    return this.connextDevicesConfig.controls.inverterList;
  }

  get connextPlantType() {
    return this.connextConfiguration.controls.plantType.value;
  }

  get is3PhasePlant() {
    return this.connextPlantType === 1;
  }

  get virtualDevices() {
    return this.virtualDevicesConfig.controls.inverterList;
  }

  get connextDevices() {
    return this.connextDevicesConfig.controls.inverterList;
  }

  get plantConfiguration() {
    return this.isConnextLoggerSelected
      ? this.connextConfiguration
      : this.isAzzurroHubLoggerSelected
        ? this.azzurroHubConfiguration
        : this.virtualConfiguration;
  }

  get isPlantConfigurationInvalidOrPending() {
    return this.plantConfiguration.invalid || this.plantConfiguration.pending;
  }

  get isUsersValid() {
    return !this.plantUsers.errors?.hasPlantOwner;
  }

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly createAwsService: CreateAwsService,
    private readonly genericDeviceValidator: GenericDeviceValidator,
    private readonly browserToastService: BrowserToastService,
    readonly elementRef: ElementRef,
    readonly renderer: Renderer,
    private readonly uiPromiseService: UiPromiseService,
    private readonly getLoggerDataQuery: GetLoggerDataQuery,
  ) {
    this.loggers = [];
    this.loggers.push(getLoggerState('azzurroHub', 3, 'assets/images/svg/azzurro-hub.svg', this.loggers, false, false, ['connext']));
    this.loggers.push(getLoggerState('others', 2, '/assets/images/svg/other-loggers.svg', this.loggers, false));
    this.loggers.push(getLoggerState('connext', 1, 'assets/images/svg/connext.svg', this.loggers, false, false, ['azzurroHub']));

    this.nextStep = this.nextStep.bind(this);
    this.previousStep = this.previousStep.bind(this);
    this.goToHome = this.goToHome.bind(this);
    this.onResetAction = this.onResetAction.bind(this);
    this.createAwsPlant = this.createAwsPlant.bind(this);
    this.populateAzzurroHubConfiguration = this.populateAzzurroHubConfiguration.bind(this);
  }

  goToHome() {
    this.wizardExit.emit();
  }

  onResetAction() {
    this.reset();
    this.onReset.emit();
  }

  nextStep() {
    this.stepper?.next();
  }

  previousStep() {
    this.stepper?.previous();
  }

  ngOnInit() {
    this.systems$?.pipe(
      untilDestroyed(this),
    ).subscribe((systems) => {
      if (systems?.length) {
        this.connextSerial.clearValidators();
        this.connextSerial.setValidators([deviceSerialValidator(['connext'])]);
        this.connextSerial.setAsyncValidators([
          this.genericDeviceValidator.deviceUnique(
            systems.filter((system) => system.source === 'aws'),
            (value) => this.genericDeviceValidator.getConnextUid(value).then((r) => r || value),
            'configuration.connextId',
          ),
          this.genericDeviceValidator.deviceTypeExists(['connext'], { connextFwMinVersion: '1.41.1954', certifiedPlant: true }),
          this.genericDeviceValidator.checkAvailableEMSWithoutCertifiedPlant()
        ]);

        this.virtualConfiguration.controls.plantName.setAsyncValidators([
          this.genericDeviceValidator.deviceUnique(
            systems,
            async (value) => value,
            'name',
          ),
        ]);

        this.connextConfiguration.controls.plantName.setAsyncValidators([
          this.genericDeviceValidator.deviceUnique(
            systems,
            async (value) => value,
            'name',
          ),
        ]);
      }
    });

    // Listener per popolamento automatico configurazione Azzurro Hub
    this.azzurroHubSerial.statusChanges
      .pipe(untilDestroyed(this))
      .subscribe(async (status) => {
        const serial = this.azzurroHubSerial.value;
        if (status === 'VALID' && serial && this.isAzzurroHubLoggerSelected) {
          await this.populateAzzurroHubConfiguration(serial.toUpperCase());
        }
      });

    // Listener per conversione automatica in uppercase dell'Azzurro Hub serial
    this.azzurroHubSerial.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value: string | null) => {
        if (value && typeof value === 'string' && value !== value.toUpperCase()) {
          // Prevent infinite loop by checking if conversion is needed
          this.azzurroHubSerial.setValue(value.toUpperCase(), { emitEvent: false });
        }
      });
  }

  reset() {
    if (!this.isConnextLoggerSelected) {
      this.monitoringForm.controls.connextSerial.setValue('');
    }
    if (!this.isAzzurroHubLoggerSelected) {
      this.monitoringForm.controls.azzurroHubSerial.setValue('');
    }

    this.monitoringForm.controls.importSystemName.setValue('');
    this.monitoringForm.markAsUntouched();

    this.virtualConfiguration.controls.plantName.setValue('');
    this.virtualConfiguration.controls.coordinates.setValue(null);
    this.virtualDevicesConfig.markAsUntouched();

    this.connextConfiguration.controls.plantName.setValue('');
    this.connextConfiguration.controls.coordinates.setValue(null);
    this.connextConfiguration.controls.plantPower.setValue(null);
    this.connextConfiguration.controls.plantType.setValue(null);
    this.connextConfiguration.controls.applied.setValue(false);
    this.connextConfiguration.controls.plantUid.setValue('');
    this.connextConfiguration.markAsUntouched();

    this.azzurroHubConfiguration.controls.plantName.setValue('');
    this.azzurroHubConfiguration.controls.coordinates.setValue(null);
    this.azzurroHubConfiguration.controls.contractualPower.setValue(null);
    this.azzurroHubConfiguration.controls.plantType.setValue(null);
    this.azzurroHubConfiguration.controls.monitoringMode.setValue(null);
    this.azzurroHubConfiguration.controls.applied.setValue(false);
    this.azzurroHubConfiguration.controls.plantUid.setValue('');
    this.azzurroHubConfiguration.markAsUntouched();

    this.connextDevicesConfig.controls.productionSensor.setValue({
      productionSensorK: null,
      id: '',
      registered: false,
    });
    this.connextDevicesConfig.controls.inverterList.clear();
    this.connextDevicesConfig.controls.inverterList.push(new FormGroup({
      inverterSerial: new FormControl('', this.connextInverterSyncValidators, this.EMSInverterAsyncValidators),
      inverterModbusId: new FormControl('', [
        Validators.required,
        Validators.min(1),
        Validators.max(255),
        Validators.pattern('^[0-9]*$'),
      ]),
      setup: new FormControl(false),
      registered: new FormControl(false),
    }));
    this.connextDevicesConfig.markAsUntouched();
    this.virtualDevicesConfig.controls.inverterList.clear();
    this.virtualDevicesConfig.controls.inverterList.push(new FormGroup({
      inverterSerial: new FormControl('', this.inverterSyncValidators, this.inverterAsyncValidators),
      added: new FormControl(false),
      dismiss: new FormControl<RemoveInverterFromPlant | null>(null),
    }));
    this.virtualDevicesConfig.markAsUntouched();

    // Reset Azzurro Hub devices array
    this.azzurroHubDevices = [];
  }

  /**
   * Popola automaticamente la configurazione Azzurro Hub usando getLoggerData
   * Popola solo i dati disponibili: maxContractualPwr, plantType, monitoringMode
   */
  async populateAzzurroHubConfiguration(serial: string): Promise<void> {
    try {
      const networkOnly = true;
      const results = await this.getLoggerDataQuery.execute(
        azzurroHubDataQuery,
        { id: serial },
        networkOnly
      );

      const data = results.data.getLoggerData as AzzurroHubDataType;

      if (data?.info?.connection) {
        this.azzurroHubConfiguration.controls.isOnline.setValue(data.info.connection.status === 'connected');
      }

      if (data?.config) {
        // Popola contractualPower da maxContractualPwr se disponibile
        if (data.config.maxContractualPwr) {
          this.azzurroHubConfiguration.controls.contractualPower.setValue(data.config.maxContractualPwr);
        }

        // Popola plantType da plantPhases
        if (data.config.plantPhases) {
          // plantPhases: "monoPhase" = 0, "threePhase" = 1
          const plantTypeValue = data.config.plantPhases === 'threePhase' ? 1 : 0;
          this.azzurroHubConfiguration.controls.plantType.setValue(plantTypeValue);
        }

        // Popola monitoringMode se disponibile
        if (data.config.monitoringMode) {
          switch (data.config.monitoringMode) {
            case AHmonitoringModeEnum.intSensor:
              this.azzurroHubConfiguration.controls.monitoringMode.setValue(MonitoringModeInput.intSensor);
              break;
            case AHmonitoringModeEnum.invMaster:
              this.azzurroHubConfiguration.controls.monitoringMode.setValue(MonitoringModeInput.invMaster);
              break;
            case AHmonitoringModeEnum.meter:
              this.azzurroHubConfiguration.controls.monitoringMode.setValue(MonitoringModeInput.meter);
              break;
            case AHmonitoringModeEnum.pwrMagic:
              this.azzurroHubConfiguration.controls.monitoringMode.setValue(MonitoringModeInput.pwrMagic);
              break;
            case AHmonitoringModeEnum.none:
              this.azzurroHubConfiguration.controls.monitoringMode.setValue(MonitoringModeInput.none);
              break;
            default:
              this.azzurroHubConfiguration.controls.monitoringMode.setValue(MonitoringModeInput.invMaster);
              break;
          }
        } else {
          // Imposta un valore di default se non specificato
          this.azzurroHubConfiguration.controls.monitoringMode.setValue(MonitoringModeInput.invMaster);
        }
      }

    } catch (error) {
      console.error('Errore nel popolamento configurazione Azzurro Hub:', error);
    }
  }

  async createAwsPlant() {
    this.uiPromiseService.run(async () => {
      const waitAfterCreate = 5 * 1000;
      const inverterList = this.isConnextLoggerSelected
        ? this.connextDevices.value.filter((device: any) => device.inverterSerial !== '')
        : this.isAzzurroHubLoggerSelected
          ? this.azzurroHubDevices.map(serial => ({ inverterSerial: serial }))
          : this.virtualDevices.value.filter((device: any) => device.inverterSerial !== '');

      let monitoringType: PlantMonitoringEnumType;

      switch (true) {
        case this.isConnextLoggerSelected && this.isOtherLoggersSelected:
          monitoringType = PlantMonitoringEnumType.CONNEXT_SOLARMAN;
          break;
        case this.isConnextLoggerSelected:
          monitoringType = PlantMonitoringEnumType.CONNEXT;
          break;
        case this.isAzzurroHubLoggerSelected && this.isOtherLoggersSelected:
          monitoringType = PlantMonitoringEnumType.AZZURRO_HUB_SOLARMAN;
          break;
        case this.isAzzurroHubLoggerSelected:
          monitoringType = PlantMonitoringEnumType.AZZURRO_HUB;
          break;
        default:
          monitoringType = PlantMonitoringEnumType.SOLARMAN;
      }

      const config: PlantConfigInput = {
        monitoringType,
      };

      const devices: CreatePlantDeviceInput[] = inverterList.map((device: any) => ({
        installationTime: new Date().getTime(),
        type: convertProductTypeNameToPlantDeviceType(new ProductModelService(device.inverterSerial).type?.name!),
        sn: new ProductModelService(device.inverterSerial).type?.name  === 'inverter' ? device.inverterSerial.toUpperCase() : device.inverterSerial,
      }));

      const users = this.plantUsers.controls.users.value
        .filter((user: any) => user.email !== '')
        .map((user: any) => ({
          email: user.email,
          userLevel: user.userLevel,
        })
      );

      /**
       * if connext or azzurro hub logger is selected, set appropriate monitoring preference
       */
      const plantUserPrefs: CreatePlantUserPrefInput = {
        plantName: this.plantConfiguration.value.plantName!,
        monitorPref: this.isConnextLoggerSelected
          ? MonitorPrefEnumType.CONNEXT
          : this.isAzzurroHubLoggerSelected
            ? MonitorPrefEnumType.AZZURRO_HUB
            : MonitorPrefEnumType.SOLARMAN,
      }

      const geo: PlantGeoInput= {
        latitude: this.plantConfiguration.value.coordinates?.latLng.lat()!,
        longitude: this.plantConfiguration.value.coordinates?.latLng.lng()!,
        IANA: this.plantConfiguration.value.coordinates?.timezone?.zoneName!,
      }

      try {

        let ems;
        if (this.isConnextLoggerSelected) {
            const connextUid = await this.genericDeviceValidator.getConnextUid(this.connextSerial.value!);
            ems = {
            sn: connextUid!,
            installationTime: new Date().getTime(),
            type: PlantEmsEnumType.CONNEXT,
          }
        } else if (this.isAzzurroHubLoggerSelected) {
            // For Azzurro Hub, use serial directly (no UID mapping needed)
            const azzurroHubSerial = this.azzurroHubSerial.value!.toUpperCase();
            ems = {
            sn: azzurroHubSerial,
            installationTime: new Date().getTime(),
            type: PlantEmsEnumType.AZZURRO_HUB,
          }
        }

        const createResponse = await this.createAwsService.createAwsPlant(devices, geo, users, plantUserPrefs, config, ems);
        if (createResponse?.plantId) {
          await wait(waitAfterCreate);
          this.wizardFinished.emit(createResponse.plantId);
        }
      } catch (error) {
        this.browserToastService.persistanceError(error as string);
      }
    },
    {
      loading: true,
    });
  }
}
